import {MainHelper} from "../../shared/models/main-helper";
import {ColumnSettingsBase} from "./column-settings-base";
import {ISlickGridColumn} from "../interfaces/slick-grid-column";
import {SlickGridComponent} from "../slick-grid.component";
import {Aggregation, AggregationSelector} from "../../stocks/models/aggregation-selector";
import {Formatters} from "./formatters";
import {TranslateService} from "@ngx-translate/core";
import * as moment from 'moment';

import {ElementRef, Renderer2} from "@angular/core";
import {ChartLineModel} from "../../buffer-graph/models/amchart/chart-line-model";
import {NumericEditor} from "./numeric-editor";
import {SelectEditor} from "./select-editor";
import SlickData = Slick.SlickData;
import {MPInsertDataChecked} from "../../marketing-purchase/models/m-p-insert-data-checked";
import {MPDistributionData} from "../../marketing-purchase/models/m-p-distribution-data";

export class ColumnsGenerator<T> {
    constructor( private readonly translateService: TranslateService,
                 private readonly renderer: Renderer2,
                 private readonly elementRef: ElementRef
    ) {

        this.helper = new MainHelper();
        this.toLower = true;
        this.minWidth = 0;
    }

    private helper: MainHelper;

    public toLower: boolean;

    public minWidth: number;

    public addCustomFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    public addSourceFilter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    public addTotalsFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;


    //# generate columns for grids in tab
    public async columnsGenerate(columns: ISlickGridColumn<T>[], columnsSettings: ColumnSettingsBase[], isAdmin): Promise<ISlickGridColumn<T>[]>{
        let toTranslate = columnsSettings.map(item => item.translateKey)
                            .concat(columnsSettings.map(item => item.descriptionKey))
                            .concat(columnsSettings.map(item => item.group));

        toTranslate = toTranslate.reduce((unique: string[], item: string) => unique.includes(item) ? unique : [...unique, item], []).filter( t => t);
        let translates = await this.translateService.get(toTranslate).toPromise();

        columnsSettings.sort((a: ColumnSettingsBase, b: ColumnSettingsBase) => {
            return a.position - b.position;
        });

        columnsSettings.forEach(item =>{
            // let column: Slick.Column<any> = {id: prop, field: prop, name: this.settings[prop].translateKey, sortable: true, width: 100, groupTotalsFormatter: this.totalsFormatter};
            let regColId = item.columnName.replace(/[\s+]|[,+]|[.+]|[(+]|[)+]|["+]|['+]/g, '_');
            let column: Slick.Column<any> = {
                id: this.toLower ? this.helper.lowerCaseFirstLetter(item.columnName) : item.columnName,
                field: this.toLower ? this.helper.lowerCaseFirstLetter(item.columnName) : item.columnName,
                name: translates[item.translateKey],
                sortable: true,
                width: item.width,
                grouping: {
                    grouped: false,
                    aggregated: false,
                    aggregation: null,
                    dropdownContainer: this.elementRef.nativeElement,
                    renderer: this.renderer
                },
                type: item.type,
                excelCellBlock: item.excelCellBlock,
                description: item.descriptionVisibility === 1 ? translates[item.descriptionKey] : '',
                descriptionKey: item.descriptionKey,
                regColId: regColId
            };

            //# add possibility apply aggregation to column from settings
            if (item.type === 'number'){
                column.grouping.aggregation = new AggregationSelector(item.columnName, translates[item.translateKey], new Aggregation(false, false, false, false));
                column.cssClass = 'text-right';
            }

            //# add possibility add current filter type to column from settings
            if (item.filterType){
                switch (item.filterType){
                    case 'dropdowndate':
                        column.dropdownFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            displayFormatter: v => Formatters.dateFormatterConv(null, null, v),
                            sortFormatter: v => v == null ? '' : moment.utc(v).format('YYYY-MM-DD HH:mm:ss')
                        };
                        break;
                    case 'dropdowndatetime':
                        column.dropdownFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            displayFormatter: v => Formatters.dateTimeFormatterConv(null, null, v),
                            sortFormatter: v => v == null ? '' : moment.utc(v).format('YYYY-MM-DD HH:mm:ss')
                        };
                        break;
                    //case 'dropdownboolean': column.dropdownFilter = { filtered: false, dropdownContainer: this.elementRef.nativeElement, renderer: this.renderer, displayFormatter: v => Formatters.booleanFormatter(null, null, v)}; break;
                    case 'dropdown':
                        column.dropdownFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                        };
                        break;
                    case 'search-string':
                        column.inputFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            type: 'string'
                        };
                        break;
                    case 'search-number':
                        column.inputFilter = {
                            filtered: false,
                            dropdownContainer: this.elementRef.nativeElement,
                            renderer: this.renderer,
                            type: 'number'
                        };
                        break;
                }
            }

            //# add formatter from settings to column
            if (item.formatter){
                switch (item.formatter){
                    case 'percent':
                        column.formatter = Formatters.percentFormatter;
                        break;
                    case 'date':
                        column.formatter = Formatters.dateFormatterConv;
                        break;
                    case 'time':
                        column.formatter = Formatters.timeFormatter;
                        break;
                    case 'datetime':
                        column.formatter = Formatters.dateTimeFormatterConv;
                        break;
                    case 'n0':
                        column.formatter = (r, c, v) => Formatters.numberFormatter(r, c, v, 0);
                        break;
                    case 'n1':
                        column.formatter = (r, c, v) => Formatters.numberFormatter(r, c, v, 1);
                        break;
                    case 'n2':
                        column.formatter = (r, c, v) => Formatters.numberFormatter(r, c, v, 2);
                        break;
                    case 'n3':
                        column.formatter = (r, c, v) => Formatters.numberFormatter(r, c, v, 3);
                        break;
                    case 'n4':
                        column.formatter = (r, c, v) => Formatters.numberFormatter(r, c, v, 4);
                        break;
                    case 'boolean':
                        column.formatter = (r, c, v) => Formatters.booleanFormatter(r, c, v);
                        break;
                }
            }

            if (item.group){
                column.group = translates[item.group];
            }

            const notAllowZeroNumberValidatorMPCheckPackage = (value, item: MPInsertDataChecked): Slick.ValidateResults => {
                if (value > 0 && (item.package_1 === null || value >= Number(item.package_1))) {
                    return {valid: true, msg: null};
                } else {
                    return {valid: false, msg: 'MP_PackageEditError'};
                }
            }

            const notAllowZeroNumberValidatorMPCheckPackage2 = (value, item: MPDistributionData): Slick.ValidateResults => {
                if (value === '0' || value === 0 || (item._package === null || value >= Number(item._package) && value % Number(item._package) === 0)) {
                    return {valid: true, msg: null};
                } else {
                    return {valid: false, msg: 'MP_PackageEditError'};
                }
            }

            //# validator
            const notAllowZeroNumberValidator = (value): Slick.ValidateResults => {
                if (value > 0) {
                    return {valid: true, msg: null};
                } else {
                    return {valid: false, msg: null};
                }
            }

            //# validator
            const allowZeroNumberValidator = (value): Slick.ValidateResults => {
                if (value > 0 || value === 0 || value === '0') {
                    return {valid: true, msg: null};
                } else {
                    return {valid: false, msg: 'NegativeEditError'};
                }
            }

            //# validator
            const allowNegativeNumberValidator = (value): Slick.ValidateResults => {
                if (value > 0 || value < 0 || value === 0 || value === '0') {
                    return {valid: true, msg: null};
                } else {
                    return {valid: false, msg: null};
                }
            }

            //# return validator
            const getValidator = (name: string): any => {
                switch (name) {
                    case 'numeric_allowzero': return allowZeroNumberValidator;
                    case 'numeric_notallowzero': return notAllowZeroNumberValidator;
                    case 'numeric_notallowzeroMPCheckPackage': return notAllowZeroNumberValidatorMPCheckPackage;
                    case 'numeric_notallowzeroMPCheckPackage2': return notAllowZeroNumberValidatorMPCheckPackage2;
                    case 'numeric_allownegative': return allowNegativeNumberValidator;
                    default: return null;
                }
            }

            //# add editor to grid cells
            if (item.edit){
                try {
                    const editor = JSON.parse(item.edit);
                    column.cssClass += ' editable-cell';

                    switch (editor.name){
                        case 'NumericEditor': column.editor = a => new NumericEditor(this.renderer, a, editor.decimalPlaces, getValidator(editor.validator), editor.allowNull);

                            break;
                        case 'StringSelectEditor':
                            editor.propName = this.toLower ? this.helper.lowerCaseFirstLetter(editor.propName) : editor.propName;
                            column.editor = a => new SelectEditor<any>(this.renderer, a, (item) =>{
                                const options: HTMLOptionElement[] = [];
                                editor.data.forEach(dt => {
                                    let option = this.renderer.createElement('option');
                                    option.text = dt;
                                    option.value = dt;
                                    option.selected = item[editor.propName] === dt;
                                    options.push(option);
                                })

                                return options;
                            }, v => v);
                            break;
                    }
                } catch (e){

                }
            }

            //# add excel block cell for file sending
            if (item.excelCellBlock){
                column.excelCellBlock = true;
            }

            //# set min width of the column
            if (this.minWidth > 0){
                column.minWidth = this.minWidth;
            }

            //# if column visible add it to grid
            if (item.visibility === 1 || item.visibility === 2 && isAdmin){
                columns.push(column);
            }
        });



        //# add custom filters or formatters to grid
        this.addCustomFormatter ? this.addCustomFormatter(columnsSettings, columns) : null;

        this.addSourceFilter ? this.addSourceFilter(columnsSettings, columns) : null;

        this.addTotalsFormatter ? this.addTotalsFormatter(columnsSettings, columns) : null;

        /*if (gridRef){
            gridRef.setColumns(columns);
        } else{
            this.helper.wait(() =>{
                if (gridRef)
                    gridRef.setColumns(columns);
            }, 100)
        }*/

        // console.log(columns, 'columns')

        return columns
    }

}



// WEBPACK FOOTER //
// ./src/app/slick-grid/models/columns-generator.ts